/**
 * ObligationDetailDrawerV2 - Enhanced drawer with full v2 schema support
 * Features: Overview, Checklist Progress, Evidence, Comments, Audit Trail, Risk tabs
 * Implements Three Superpowers: Cross-Framework Intelligence, Ownership, Audit Trail
 */
import React, { useState, useEffect } from 'react';
import { 
  X, 
  CheckCircle2, 
  Clock, 
  User, 
  FileText, 
  MessageSquare, 
  Shield,
  Link as LinkIcon,
  Upload,
  Loader2,
  AlertTriangle,
  ChevronRight,
  CheckSquare,
  Square,
  AlertCircle,
  Target,
  Users,
  Calendar,
  Play,
  Pause,
  RotateCcw,
  Send,
  Flag,
  ArrowUpRight
} from 'lucide-react';

import { apiFetch } from '../../services/api_service';

const PRIORITY_COLORS = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  medium: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  low: 'bg-[#2F2F2F] text-[#6B6B6B] border-[#2F2F2F]',
};

const STATUS_COLORS = {
  // Lifecycle vocabulary (spec 2026-07-12)
  identified: 'bg-[#2F2F2F] text-[#A0A0A0]',
  assigned: 'bg-blue-500/20 text-blue-400',
  in_progress: 'bg-amber-500/20 text-amber-400',
  evidence_attached: 'bg-purple-500/20 text-purple-400',
  validated: 'bg-emerald-500/20 text-emerald-400',
  locked: 'bg-cyan-500/20 text-cyan-400',
  auditable: 'bg-emerald-500/20 text-emerald-400',
  breached: 'bg-red-500/20 text-red-400',
  cancelled: 'bg-[#2F2F2F] text-[#6B6B6B]',
  // Legacy vocabulary (pre-migration docs)
  completed: 'bg-emerald-500/20 text-emerald-400',
  not_started: 'bg-[#2F2F2F] text-[#6B6B6B]',
  pending: 'bg-amber-500/20 text-amber-400',
  blocked: 'bg-red-500/20 text-red-400',
  overdue: 'bg-red-500/20 text-red-400',
};

// Client mirror of the backend transition matrix — initial button rendering
// only; the server response's allowed_next_transitions wins after any action.
const ALLOWED_TRANSITIONS = {
  identified: ['assigned', 'breached', 'cancelled'],
  assigned: ['in_progress', 'breached', 'cancelled'],
  in_progress: ['evidence_attached', 'breached', 'cancelled'],
  evidence_attached: ['in_progress', 'validated', 'breached', 'cancelled'],
  validated: ['evidence_attached', 'locked', 'breached', 'cancelled'],
  locked: ['auditable', 'breached', 'cancelled'],
  auditable: ['breached', 'cancelled'],
  breached: [],
  cancelled: [],
};

const TRANSITION_LABELS = {
  assigned: 'Assign',
  in_progress: 'Start Working',
  evidence_attached: 'Attach Evidence',
  validated: 'Validate',
  locked: 'Lock',
  auditable: 'Mark Auditable',
  breached: 'Mark Breached',
  cancelled: 'Cancel',
};

const CHECKLIST_STATUS_ICONS = {
  completed: CheckCircle2,
  in_progress: Play,
  blocked: AlertCircle,
  not_started: Square,
  skipped: RotateCcw,
};

export default function ObligationDetailDrawerV2({ obligationId, open, onClose, onUpdate }) {
  const [obligation, setObligation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [updatingItem, setUpdatingItem] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [allowedNext, setAllowedNext] = useState(null);
  const [transitionReason, setTransitionReason] = useState('');
  const [transitionError, setTransitionError] = useState(null);

  useEffect(() => {
    if (obligationId && open) {
      setAllowedNext(null);
      setTransitionError(null);
      setTransitionReason('');
      fetchObligation();
    }
  }, [obligationId, open]);

  const fetchObligation = async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/v1/obligations-v2/${obligationId}`);
      if (data && !data._error) {
        setObligation(data);
      }
    } catch (err) {
      console.error('Error fetching obligation:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateChecklistItem = async (itemId, newStatus) => {
    setUpdatingItem(itemId);
    try {
      const data = await apiFetch(
        `/v1/obligations-v2/${obligationId}/checklist/${itemId}`,
        { method: 'PUT', body: JSON.stringify({ status: newStatus }) }
      );
      if (data && !data._error) {
        await fetchObligation();
        if (onUpdate) onUpdate();
      }
    } catch (err) {
      console.error('Error updating checklist item:', err);
    } finally {
      setUpdatingItem(null);
    }
  };

  const handleTransition = async (newStatus) => {
    setTransitionError(null);
    if (newStatus === 'breached' && !transitionReason.trim()) {
      setTransitionError('A reason is required to mark an obligation breached.');
      return;
    }
    const data = await apiFetch(`/v1/obligations-v2/${obligationId}/status`, {
      method: 'PUT',
      body: JSON.stringify({
        status: newStatus,
        expected_current_status: obligation?.status || null,
        version: obligation?.version ?? null,
        reason: transitionReason.trim() || null,
        idempotency_key:
          (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
          `${obligationId}-${newStatus}-${Date.now()}`,
      }),
    });
    if (data && !data._error) {
      setAllowedNext(data.allowed_next_transitions || null);
      setTransitionReason('');
      await fetchObligation();
      if (onUpdate) onUpdate();
    } else if (data?.status === 409) {
      setTransitionError('Obligation changed since you loaded it — refreshed to the latest state.');
      await fetchObligation();
    } else {
      setTransitionError(
        typeof data?.message === 'string'
          ? data.message
          : 'Transition rejected.');
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    setSubmittingComment(true);
    try {
      const params = new URLSearchParams({
        text: commentText,
        is_internal: 'true'
      });
      const data = await apiFetch(
        `/v1/obligations-v2/${obligationId}/comments?${params}`,
        { method: 'POST' }
      );
      if (data && !data._error) {
        setCommentText('');
        await fetchObligation();
      }
    } catch (err) {
      console.error('Error adding comment:', err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric' 
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-US', { 
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (!open) return null;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Target },
    { id: 'checklist', label: 'Checklist', icon: CheckSquare },
    { id: 'evidence', label: 'Evidence', icon: FileText },
    { id: 'comments', label: 'Comments', icon: MessageSquare },
    { id: 'audit', label: 'Audit', icon: Shield },
  ];

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 z-40 transition-opacity"
        onClick={onClose}
        data-testid="drawer-backdrop"
      />
      
      {/* Drawer */}
      <div 
        className="fixed inset-y-0 right-0 w-full max-w-2xl bg-[#0A0A0A] border-l border-[#1F1F1F] z-50 flex flex-col overflow-hidden"
        data-testid="obligation-detail-drawer-v2"
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>
        ) : !obligation ? (
          <div className="flex flex-col items-center justify-center h-full">
            <AlertTriangle className="h-12 w-12 text-red-400 mb-4" />
            <p className="text-[#6B6B6B]">Obligation not found</p>
            <button onClick={onClose} className="mt-4 text-emerald-400 hover:underline">
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-6 py-4 border-b border-[#1F1F1F] bg-[#111111] shrink-0">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="px-2 py-0.5 text-xs font-mono bg-emerald-500/20 text-emerald-400 rounded">
                      {obligation.obligation_code}
                    </span>
                    <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded border ${PRIORITY_COLORS[obligation.priority] || PRIORITY_COLORS.medium}`}>
                      {obligation.priority}
                    </span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded capitalize ${STATUS_COLORS[obligation.status] || STATUS_COLORS.not_started}`}>
                      {(obligation.status || 'not_started').replace(/_/g, ' ')}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-white leading-tight">
                    {obligation.obligation_title}
                  </h2>
                  {obligation.source?.framework_code && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="px-2 py-1 text-xs bg-[#1F1F1F] text-[#A0A0A0] rounded">
                        {obligation.source.framework_code.replace('_', '/')}
                      </span>
                      {obligation.source.requirement_code && (
                        <span className="text-xs text-[#6B6B6B]">
                          {obligation.source.requirement_code}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <button 
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-[#1F1F1F] text-[#6B6B6B] hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Progress Bar */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[#A0A0A0]">Progress</span>
                  <span className="text-sm font-semibold text-white">
                    {Math.round(obligation.progress?.percent_complete || 0)}%
                  </span>
                </div>
                <div className="h-2 bg-[#1F1F1F] rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      (obligation.progress?.percent_complete || 0) === 100 
                        ? 'bg-emerald-500' 
                        : 'bg-gradient-to-r from-emerald-500 to-teal-500'
                    }`}
                    style={{ width: `${obligation.progress?.percent_complete || 0}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-2 text-xs text-[#6B6B6B]">
                  <span>
                    {(obligation.progress?.checklist_items || []).filter(i => i.status === 'completed').length} of {(obligation.progress?.checklist_items || []).length} tasks
                  </span>
                  {obligation.timeline?.due_date && (
                    <span className={
                      obligation.timeline?.days_until_due < 0 ? 'text-red-400' :
                      obligation.timeline?.days_until_due <= 7 ? 'text-amber-400' : ''
                    }>
                      {obligation.timeline?.days_until_due < 0 
                        ? `${Math.abs(obligation.timeline?.days_until_due)} days overdue`
                        : `Due in ${obligation.timeline?.days_until_due} days`}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="px-6 pt-2 border-b border-[#1F1F1F] shrink-0 bg-[#0D0D0D]">
              <div className="flex gap-1 overflow-x-auto">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 py-2.5 text-sm font-medium rounded-t-lg flex items-center gap-2 whitespace-nowrap transition-colors ${
                        activeTab === tab.id 
                          ? 'bg-[#1F1F1F] text-white' 
                          : 'text-[#6B6B6B] hover:text-white hover:bg-[#151515]'
                      }`}
                    >
                      <Icon size={14} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Description */}
                  <div>
                    <h3 className="text-sm font-semibold text-white mb-2">Description</h3>
                    <p className="text-sm text-[#A0A0A0] leading-relaxed">
                      {obligation.obligation_description || 'No description provided'}
                    </p>
                  </div>

                  {/* Ownership Info */}
                  <div className="p-4 rounded-xl bg-[#111111] border border-[#1F1F1F]">
                    <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                      <Users size={16} className="text-blue-400" />
                      Ownership
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-[#6B6B6B] mb-1">Owner</p>
                        <p className="text-sm font-medium text-white">
                          {obligation.ownership?.owner_name || 'Unassigned'}
                        </p>
                        {obligation.ownership?.owner_role && (
                          <p className="text-xs text-[#6B6B6B]">{obligation.ownership.owner_role}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-[#6B6B6B] mb-1">Department</p>
                        <p className="text-sm text-white">
                          {obligation.ownership?.owner_department || '-'}
                        </p>
                      </div>
                    </div>
                    {obligation.ownership?.escalation_chain?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-[#1F1F1F]">
                        <p className="text-xs text-[#6B6B6B] mb-2">Escalation Chain</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {obligation.ownership.escalation_chain.map((esc, idx) => (
                            <span key={idx} className="px-2 py-1 text-xs bg-[#1F1F1F] text-[#A0A0A0] rounded flex items-center gap-1">
                              L{esc.level}: {esc.user_name || esc.user_role}
                              <ChevronRight size={12} className="text-[#4A4A4A]" />
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Timeline */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-[#111111] border border-[#1F1F1F] rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar size={14} className="text-[#6B6B6B]" />
                        <p className="text-xs text-[#6B6B6B]">Due Date</p>
                      </div>
                      <p className="text-sm font-medium text-white">
                        {formatDate(obligation.timeline?.due_date)}
                      </p>
                    </div>
                    <div className="p-4 bg-[#111111] border border-[#1F1F1F] rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock size={14} className="text-[#6B6B6B]" />
                        <p className="text-xs text-[#6B6B6B]">Estimated Hours</p>
                      </div>
                      <p className="text-sm font-medium text-white">
                        {obligation.timeline?.estimated_hours || 0}h
                      </p>
                    </div>
                  </div>

                  {/* Cross-Framework Intelligence (Superpower #1) */}
                  {obligation.source?.equivalent_requirements?.length > 0 && (
                    <div className="p-4 rounded-xl bg-teal-500/10 border border-teal-500/30">
                      <h3 className="text-sm font-semibold text-teal-400 mb-3 flex items-center gap-2">
                        <LinkIcon size={16} />
                        Cross-Framework Intelligence
                      </h3>
                      <p className="text-sm text-teal-300 mb-3">
                        This obligation also satisfies requirements in:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {obligation.source.equivalent_requirements.map((eq, idx) => (
                          <span key={idx} className="px-3 py-1.5 text-xs bg-teal-500/20 text-teal-400 rounded-lg flex items-center gap-2">
                            <span className="font-medium">{eq.framework_code}</span>
                            <span className="text-teal-500/70">{eq.requirement_code}</span>
                            {eq.equivalence_score && (
                              <span className="px-1.5 py-0.5 bg-teal-500/30 rounded text-[10px]">
                                {Math.round(eq.equivalence_score * 100)}%
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Risk Level */}
                  {obligation.risk && (
                    <div className={`p-4 rounded-xl border ${
                      obligation.risk.risk_level === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                      obligation.risk.risk_level === 'high' ? 'bg-amber-500/10 border-amber-500/30' :
                      'bg-[#111111] border-[#1F1F1F]'
                    }`}>
                      <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                        <Flag size={14} className={
                          obligation.risk.risk_level === 'critical' ? 'text-red-400' :
                          obligation.risk.risk_level === 'high' ? 'text-amber-400' : 'text-[#6B6B6B]'
                        } />
                        Risk Level: <span className="capitalize">{obligation.risk.risk_level}</span>
                      </h3>
                      {obligation.risk.impact_if_missed && (
                        <p className="text-sm text-[#A0A0A0]">{obligation.risk.impact_if_missed}</p>
                      )}
                    </div>
                  )}

                  {/* Status Actions — server-driven allowed transitions.
                      Legacy-vocabulary docs (pre-migration) show no actions;
                      the backend enforces the matrix either way. */}
                  <div className="pt-4 border-t border-[#1F1F1F] space-y-2">
                    {transitionError && (
                      <div className="flex items-center gap-2 text-sm text-red-400">
                        <AlertCircle size={14} /> {transitionError}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2">
                      {(allowedNext ?? ALLOWED_TRANSITIONS[obligation.status] ?? []).map((next) => (
                        <button
                          key={next}
                          onClick={() => handleTransition(next)}
                          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 border ${
                            next === 'breached'
                              ? 'bg-red-500/10 hover:bg-red-500/20 border-red-500/30 text-red-400'
                              : next === 'cancelled'
                              ? 'bg-[#1F1F1F] hover:bg-[#2F2F2F] border-[#2F2F2F] text-[#A0A0A0]'
                              : 'bg-blue-500/20 hover:bg-blue-500/30 border-blue-500/30 text-blue-400'
                          }`}
                        >
                          {next === 'breached' ? <Flag size={14} /> :
                           next === 'cancelled' ? <X size={14} /> :
                           next === 'validated' || next === 'auditable' ? <CheckCircle2 size={14} /> :
                           <Play size={14} />}
                          {TRANSITION_LABELS[next] || next.replace(/_/g, ' ')}
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      value={transitionReason}
                      onChange={(e) => setTransitionReason(e.target.value)}
                      placeholder="Reason (required for breach, recorded on the audit ledger)"
                      className="w-full px-3 py-2 bg-[#1F1F1F] border border-[#2F2F2F] rounded-lg text-sm text-white placeholder-[#6B6B6B] focus:outline-none focus:border-blue-500/50"
                    />
                  </div>
                </div>
              )}

              {/* Checklist Tab */}
              {activeTab === 'checklist' && (
                <div className="space-y-4">
                  {/* Checklist Header */}
                  <div className="flex items-center justify-between pb-4 border-b border-[#1F1F1F]">
                    <div>
                      <h3 className="text-sm font-semibold text-white">Task Checklist</h3>
                      <p className="text-xs text-[#6B6B6B] mt-1">
                        Complete tasks to track progress
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-emerald-400">
                        {(obligation.progress?.checklist_items || []).filter(i => i.status === 'completed').length}
                      </span>
                      <span className="text-lg text-[#4A4A4A]">/</span>
                      <span className="text-lg text-[#6B6B6B]">
                        {(obligation.progress?.checklist_items || []).length}
                      </span>
                    </div>
                  </div>

                  {/* Checklist Items */}
                  {(obligation.progress?.checklist_items || []).map((item, idx) => {
                    const StatusIcon = CHECKLIST_STATUS_ICONS[item.status] || Square;
                    const isCompleted = item.status === 'completed';
                    const isInProgress = item.status === 'in_progress';
                    const isBlocked = item.status === 'blocked';
                    
                    return (
                      <div
                        key={item.id || idx}
                        className={`p-4 rounded-xl border transition-all ${
                          isCompleted 
                            ? 'bg-emerald-500/10 border-emerald-500/30'
                            : isInProgress
                            ? 'bg-blue-500/10 border-blue-500/30'
                            : isBlocked
                            ? 'bg-red-500/10 border-red-500/30'
                            : 'bg-[#111111] border-[#1F1F1F] hover:border-[#2F2F2F]'
                        }`}
                        data-testid={`checklist-item-${item.id}`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Status Toggle */}
                          <div className="flex flex-col items-center gap-1 pt-0.5">
                            <button
                              onClick={() => {
                                if (isCompleted) {
                                  handleUpdateChecklistItem(item.id, 'not_started');
                                } else if (isInProgress) {
                                  handleUpdateChecklistItem(item.id, 'completed');
                                } else {
                                  handleUpdateChecklistItem(item.id, 'in_progress');
                                }
                              }}
                              disabled={updatingItem === item.id}
                              className="group"
                              title={isCompleted ? 'Mark incomplete' : isInProgress ? 'Mark complete' : 'Start task'}
                            >
                              {updatingItem === item.id ? (
                                <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
                              ) : isCompleted ? (
                                <CheckCircle2 className="h-6 w-6 text-emerald-400 group-hover:text-emerald-300" />
                              ) : isInProgress ? (
                                <div className="h-6 w-6 rounded-full border-2 border-blue-400 bg-blue-400/30 flex items-center justify-center group-hover:bg-blue-400/50">
                                  <div className="h-2 w-2 rounded-full bg-blue-400" />
                                </div>
                              ) : (
                                <div className="h-6 w-6 rounded-full border-2 border-[#4A4A4A] group-hover:border-emerald-500 transition-colors" />
                              )}
                            </button>
                            <span className="text-[10px] text-[#4A4A4A]">{item.order || idx + 1}</span>
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className={`text-sm font-medium leading-tight ${
                                isCompleted ? 'text-emerald-400 line-through' : 'text-white'
                              }`}>
                                {item.title}
                              </p>
                              {item.is_mandatory === false && (
                                <span className="px-1.5 py-0.5 text-[10px] bg-[#1F1F1F] text-[#6B6B6B] rounded shrink-0">
                                  Optional
                                </span>
                              )}
                            </div>
                            
                            {item.description && (
                              <p className="text-xs text-[#6B6B6B] mt-1">
                                {item.description}
                              </p>
                            )}

                            {/* Meta info */}
                            <div className="flex items-center gap-4 mt-2 flex-wrap">
                              {item.assigned_to_name && (
                                <span className="text-xs text-[#6B6B6B] flex items-center gap-1">
                                  <User size={10} />
                                  {item.assigned_to_name}
                                </span>
                              )}
                              {item.due_date && (
                                <span className="text-xs text-[#6B6B6B] flex items-center gap-1">
                                  <Calendar size={10} />
                                  {formatDate(item.due_date)}
                                </span>
                              )}
                              {item.estimated_minutes && (
                                <span className="text-xs text-[#6B6B6B] flex items-center gap-1">
                                  <Clock size={10} />
                                  {item.estimated_minutes}m
                                </span>
                              )}
                              {isCompleted && item.completed_at && (
                                <span className="text-xs text-emerald-400/70">
                                  Completed {formatDate(item.completed_at)}
                                </span>
                              )}
                            </div>

                            {/* Attachments/Notes */}
                            {item.attachments?.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-[#1F1F1F]">
                                {item.attachments.filter(a => a.type === 'note' || a.type === 'progress_note').map((att, i) => (
                                  <p key={i} className="text-xs text-[#A0A0A0] italic">
                                    {att.content}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Quick Actions */}
                          {!isCompleted && (
                            <div className="flex gap-1 shrink-0">
                              {!isInProgress && (
                                <button
                                  onClick={() => handleUpdateChecklistItem(item.id, 'in_progress')}
                                  className="p-1.5 rounded hover:bg-blue-500/20 text-[#6B6B6B] hover:text-blue-400 transition-colors"
                                  title="Start task"
                                >
                                  <Play size={14} />
                                </button>
                              )}
                              <button
                                onClick={() => handleUpdateChecklistItem(item.id, 'completed')}
                                className="p-1.5 rounded hover:bg-emerald-500/20 text-[#6B6B6B] hover:text-emerald-400 transition-colors"
                                title="Mark complete"
                              >
                                <CheckCircle2 size={14} />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Dependencies indicator */}
                        {item.blocked_by?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-[#1F1F1F]">
                            <span className="text-xs text-amber-400 flex items-center gap-1">
                              <AlertCircle size={12} />
                              Blocked by: {item.blocked_by.join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {(!obligation.progress?.checklist_items || obligation.progress.checklist_items.length === 0) && (
                    <div className="text-center py-12 bg-[#111111] border border-[#1F1F1F] rounded-xl">
                      <CheckSquare size={48} className="mx-auto text-[#2F2F2F] mb-4" />
                      <h3 className="text-sm font-medium text-white mb-1">No checklist items</h3>
                      <p className="text-xs text-[#6B6B6B]">This obligation doesn't have any tasks defined</p>
                    </div>
                  )}
                </div>
              )}

              {/* Evidence Tab */}
              {activeTab === 'evidence' && (
                <div className="space-y-4">
                  <div className="pb-4 border-b border-[#1F1F1F]">
                    <h3 className="text-sm font-semibold text-white">Required Evidence</h3>
                    <p className="text-xs text-[#6B6B6B] mt-1">
                      Upload and manage evidence to complete this obligation
                    </p>
                  </div>

                  {(obligation.completion_criteria?.required_evidence || []).map((ev, idx) => (
                    <div key={idx} className="p-4 rounded-xl border border-[#1F1F1F] bg-[#111111]">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-white mb-1 capitalize">
                            {ev.evidence_type?.replace(/_/g, ' ') || 'Document'}
                          </p>
                          <p className="text-xs text-[#6B6B6B]">
                            {ev.description || 'Required evidence'}
                          </p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded ${
                          ev.status === 'complete'
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : ev.status === 'in_progress'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {ev.status === 'complete' ? 'Uploaded' : ev.status === 'in_progress' ? 'In Progress' : 'Missing'}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <button className="flex-1 py-2 px-4 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2">
                          <Upload size={14} />
                          Upload {ev.is_mandatory ? '' : '(Optional)'}
                        </button>
                        <button className="py-2 px-4 bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#A0A0A0] text-sm font-medium rounded-lg transition-colors flex items-center gap-2">
                          <LinkIcon size={14} />
                          Link Existing
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {(!obligation.completion_criteria?.required_evidence || 
                    obligation.completion_criteria.required_evidence.length === 0) && (
                    <div className="text-center py-12 bg-[#111111] border border-[#1F1F1F] rounded-xl">
                      <FileText className="h-12 w-12 text-[#2F2F2F] mx-auto mb-4" />
                      <h3 className="text-sm font-medium text-white mb-1">No evidence requirements</h3>
                      <p className="text-xs text-[#6B6B6B]">This obligation doesn't require specific evidence</p>
                    </div>
                  )}
                </div>
              )}

              {/* Comments Tab */}
              {activeTab === 'comments' && (
                <div className="space-y-4">
                  {(obligation.comments || []).map((comment, idx) => (
                    <div key={idx} className="p-4 rounded-xl bg-[#111111] border border-[#1F1F1F]">
                      <div className="flex items-start gap-3">
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-500/30 to-teal-500/30 flex items-center justify-center shrink-0">
                          <User size={14} className="text-emerald-400" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-medium text-white">
                              {comment.user_name || 'Unknown User'}
                            </p>
                            {comment.user_role && (
                              <span className="text-xs text-[#6B6B6B]">• {comment.user_role}</span>
                            )}
                            <span className="text-xs text-[#4A4A4A] ml-auto">
                              {formatDate(comment.created_at)} {formatTime(comment.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-[#A0A0A0]">{comment.text}</p>
                          
                          {/* Replies */}
                          {comment.replies?.length > 0 && (
                            <div className="mt-3 pl-4 border-l-2 border-[#1F1F1F] space-y-2">
                              {comment.replies.map((reply, rIdx) => (
                                <div key={rIdx} className="text-sm">
                                  <span className="text-white font-medium">{reply.user_name}</span>
                                  <span className="text-[#A0A0A0] ml-2">{reply.text}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {(!obligation.comments || obligation.comments.length === 0) && (
                    <div className="text-center py-8">
                      <MessageSquare className="h-10 w-10 text-[#2F2F2F] mx-auto mb-3" />
                      <p className="text-sm text-[#6B6B6B]">No comments yet</p>
                    </div>
                  )}

                  {/* Add Comment */}
                  <div className="mt-4 pt-4 border-t border-[#1F1F1F]">
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Add a comment..."
                      className="w-full px-4 py-3 bg-[#111111] border border-[#1F1F1F] rounded-xl text-sm text-white placeholder:text-[#4A4A4A] focus:border-emerald-500/50 focus:outline-none resize-none"
                      rows={3}
                    />
                    <div className="flex justify-end mt-2">
                      <button 
                        onClick={handleAddComment}
                        disabled={!commentText.trim() || submittingComment}
                        className="px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {submittingComment ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Send size={14} />
                        )}
                        Post Comment
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Audit Tab */}
              {activeTab === 'audit' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Shield size={20} className="text-purple-400" />
                      <h3 className="text-sm font-semibold text-purple-400">
                        Immutable Audit Trail
                      </h3>
                    </div>
                    <p className="text-sm text-purple-300">
                      Complete audit history for this obligation showing all status changes,
                      updates, and actions taken.
                    </p>
                  </div>

                  {/* Status History */}
                  <div>
                    <h4 className="text-sm font-medium text-white mb-3">Status History</h4>
                    <div className="space-y-2">
                      {(obligation.status_history || []).slice().reverse().map((entry, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-[#111111] border border-[#1F1F1F] rounded-xl">
                          <div className={`h-3 w-3 rounded-full ${
                            entry.status === 'completed' ? 'bg-emerald-500' :
                            entry.status === 'overdue' ? 'bg-red-500' :
                            entry.status === 'in_progress' ? 'bg-blue-500' :
                            entry.status === 'blocked' ? 'bg-red-500' : 'bg-amber-500'
                          }`} />
                          <span className="text-sm text-white capitalize flex-1">
                            {entry.status?.replace(/_/g, ' ')}
                          </span>
                          {entry.duration_seconds && (
                            <span className="text-xs text-[#4A4A4A]">
                              {Math.round(entry.duration_seconds / 3600)}h in status
                            </span>
                          )}
                          <span className="text-xs text-[#6B6B6B]">
                            {formatDate(entry.entered_at)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="pt-4 border-t border-[#1F1F1F]">
                    <h4 className="text-sm font-medium text-white mb-3">Metadata</h4>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div className="p-3 bg-[#111111] border border-[#1F1F1F] rounded-lg">
                        <p className="text-[#6B6B6B]">Created</p>
                        <p className="text-white mt-1">{formatDate(obligation.created_at)}</p>
                        <p className="text-[#4A4A4A]">by {obligation.created_by_name || 'System'}</p>
                      </div>
                      <div className="p-3 bg-[#111111] border border-[#1F1F1F] rounded-lg">
                        <p className="text-[#6B6B6B]">Last Updated</p>
                        <p className="text-white mt-1">{formatDate(obligation.updated_at)}</p>
                        <p className="text-[#4A4A4A]">by {obligation.updated_by_name || 'System'}</p>
                      </div>
                      <div className="p-3 bg-[#111111] border border-[#1F1F1F] rounded-lg">
                        <p className="text-[#6B6B6B]">Version</p>
                        <p className="text-white mt-1">v{obligation.version || 1}</p>
                      </div>
                      <div className="p-3 bg-[#111111] border border-[#1F1F1F] rounded-lg">
                        <p className="text-[#6B6B6B]">Obligation ID</p>
                        <p className="text-white mt-1 font-mono text-[10px] break-all">{obligation.id}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
